import { OWNERS, type MergePlan } from "../actions";

/**
 * What a merge will do, shown before it is done.
 *
 * Shared by the Duplicates queue and the person screen's "merge with…" action,
 * so the two can never describe the same merge differently. The plan comes from
 * `previewMergePeople`; nothing here computes consequences of its own.
 *
 * A queue merge of three or more records passes one plan per record dropped,
 * and they are listed in the order they will run.
 */
export function MergePlanView({
  plans,
  busy,
  err,
  onCommit,
  onBack,
}: {
  plans: MergePlan[];
  busy: boolean;
  err: string | null;
  onCommit: () => void;
  onBack: () => void;
}) {
  const keepName = plans[0]?.keepName ?? "";
  const blockers = plans.flatMap((p) => p.blockers);

  return (
    <div className="ask">
      <p className="ask-q">מה יקרה</p>
      <p className="muted">
        {plans.map((p, i) => (
          <span key={p.dropId}>
            {i > 0 && ", "}
            <b>„{p.dropName}”</b>
          </span>
        ))}{" "}
        {plans.length > 1 ? "יימחקו" : "יימחק"}, ו־<b>„{keepName}”</b> יישאר עם כל מה שהיה בכולם.
      </p>
      {plans.map((plan) => (
        <ul className="merge-gains" key={plan.dropId}>
          {plans.length > 1 && <li className="faint">מתוך „{plan.dropName}”:</li>}
          {plan.gains.phones.length > 0 && (
            <li>
              טלפון שנוסיף: <span className="mono">{plan.gains.phones.join(", ")}</span>
            </li>
          )}
          {plan.gains.emails.length > 0 && (
            <li>
              מייל שנוסיף: <span className="mono">{plan.gains.emails.join(", ")}</span>
            </li>
          )}
          {plan.gains.activities > 0 && <li>{plan.gains.activities} פעילויות בציר הזמן יעברו</li>}
          {plan.gains.donations > 0 && <li>{plan.gains.donations} תרומות יעברו לכרטיס שנשאר</li>}
          {plan.gains.interactions > 0 && <li>{plan.gains.interactions} תיעודי קשר יעברו</li>}
          {plan.gains.tags > 0 && <li>{plan.gains.tags} תגיות יתווספו</li>}
          {plan.gains.owners.length > 0 && (
            <li>
              שיוך שנוסיף:{" "}
              {plan.gains.owners.map((o) => OWNERS.find((x) => x.slug === o)?.label ?? o).join(", ")}
            </li>
          )}
          {plan.gains.fillsFields.length > 0 && (
            <li>נשלים שדות חסרים: {plan.gains.fillsFields.map(fieldLabel).join(", ")}</li>
          )}
        </ul>
      ))}

      {blockers.length > 0 && (
        <div className="notice error" style={{ margin: "8px 0 0" }}>
          {blockers.join(" · ")}
        </div>
      )}

      <p className="faint" style={{ marginTop: 8 }}>
        אנחנו שומרים ביומן הביקורת גיבוי מלא של הכרטיס שנמחק, יחד עם רשימה של כל מה שהועבר ממנו.
      </p>

      <div className="btn-row" style={{ marginTop: 10 }}>
        <button className="btn btn-primary" onClick={onCommit} disabled={busy}>
          {busy ? "מאחדים…" : "איחוד"}
        </button>
        <button className="btn" onClick={onBack} disabled={busy}>
          חזרה
        </button>
      </div>
      {err && <div className="notice error">{err}</div>}
    </div>
  );
}

function fieldLabel(f: string): string {
  const map: Record<string, string> = {
    address: "כתובת",
    gregorianBirthDate: "תאריך לידה",
    hebrewBirthDateRaw: "תאריך עברי",
    gender: "מגדר",
    organization: "עסק",
    education: "מסגרת חינוכית",
    householdId: "משפחה",
    householdRole: "תפקיד במשפחה",
    barMitzvahInfo: "בר מצווה",
    lastMeaningfulContactAt: "מתי דיברתם",
  };
  return map[f] ?? f;
}
